import React from 'react';
import * as PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import { parse as parseUrl } from 'url';
import loadable from '@loadable/component';
import { connect } from '../../connect';
import { getMediaItem, setItemProgress, createJoinGroupMembership, createFollowGroupMembership } from '../../actions';
import { launch7Campaign } from '../../utils/campaigns';
import { PlusRibbon } from '../../components/plus-ribbon';
import { LoadingIndicator } from '../../components/loading-indicator';
import { EndCap } from '../../components/end-cap';
import { UpNext } from '../../components/up-next';
import { RatingOverlay, KidsRatingOverlay } from '../../components/rating-overlay';
import { ShareOverlay } from '../../components/share-overlay';
import logger from '../../utils/logger';
import { createItemOpenGraphMetadata, createOembedLink } from '../../helpers/metadata-helper';
import { upsells, defaultUpsell } from '../../data/upsells';
import { getCoverUrl, getItemCover } from '../../utils/item-utils';
import './index.less';
import './branding-bug.scss';
import './live-viewer-count.scss';
import { formatUrl, convertStringToBoolean } from '../../utils/url';
import { IAuthState } from '../../reducers/auth-reducers';
import { ISubscriptionState } from '../../reducers/subscription-reducers';
import { IKidsModeState } from '../../reducers/kids-mode-reducers';
import { IGroupMembershipState } from '../../reducers/group-membership-reducers';
import { IReferrerState } from '../../reducers/referrer-reducers';
import { IItemState, IGroupInfo } from '../../reducers/item-reducers';
import { IMediaItemState } from '../../reducers/media-reducers';
import { IReduxProps } from '../../actions/action-types';
import baseUrls from '../../data/baseUrls';

const VideoPlayer: React.ComponentType<any> = loadable(() => import('./video-player'), {
	fallback: <LoadingIndicator />,
	ssr: false,
});

function mapStateToProps(
	{ auth, item, mediaItem, referrer, subscription, groupMembership, kidsMode },
	{
		match: {
			params: { itemId, assetId, resourceId, mediaId },
		},
		location: { search },
	}
) {
	const {
		embedded,
		autoplay,
		playlistId,
		resume,
		noBug,
		noRating,
		accentColor,
		t,
	}: {
		embedded?: string;
		autoplay?: string;
		playlistId?: string;
		resume?: string;
		noBug?: string;
		noRating?: string;
		accentColor?: string;
		t?: string;
	} = search ? parseUrl(search, true).query : {};
	resourceId = resourceId ? resourceId.toUpperCase() : null;
	mediaId = mediaId ? mediaId.toUpperCase() : null;
	const isEmbedded =
		embedded !== undefined
			? convertStringToBoolean(embedded)
			: autoplay !== undefined
			? convertStringToBoolean(autoplay)
			: false;
	const isAutoplay = autoplay !== undefined ? convertStringToBoolean(autoplay) : true;
	const startTime = parseTime(t);
	return {
		auth,
		item,
		mediaItem,
		referrer,
		subscription,
		itemId,
		assetId,
		resourceId,
		mediaId,
		playlistId,
		isEmbedded,
		isAutoplay,
		resume: resume !== 'false',
		groupMembership,
		kidsMode,
		noBug: !!noBug && convertStringToBoolean(noBug),
		noRating: !!noRating && convertStringToBoolean(noRating),
		accentColor: accentColor && (/[0-9A-Fa-f]{6}/.test(accentColor) ? accentColor.toUpperCase() : undefined),
		startTime,
	};
}

function parseTime(t) {
	if (!t) {
		return undefined;
	}

	const match = /(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)/.exec(t);
	if (!match) {
		return undefined;
	}

	let time = 0;
	if (match[3]) {
		time += Number.parseInt(match[3], 10);
	}
	if (match[2]) {
		time += Number.parseInt(match[2], 10) * 60;
	}
	if (match[1]) {
		time += Number.parseInt(match[1], 10) * 60;
	}

	return time;
}

interface IMediaPageProps extends IReduxProps {
	auth: IAuthState;
	item: IItemState;
	mediaItem: IMediaItemState;
	location: URL;
	itemId?: string;
	assetId?: string;
	resourceId?: string;
	mediaId?: string;
	isEmbedded: boolean;
	isAutoplay: boolean;
	playlistId?: string;
	subscription: ISubscriptionState;
	resume: boolean;
	referrer: IReferrerState;
	groupMembership: IGroupMembershipState;
	noBug: boolean;
	noRating: boolean;
	accentColor?: string;
	startTime?: number;
	kidsMode: IKidsModeState;
}

interface IMediaPageState {
	retryGetMedia: boolean;
	needsRemount: boolean;
	autoplayingPlaylist: boolean;
	fromFaithlifeTv?: boolean;
	supportsMp4?: boolean;
	reloadItemTimeout?: NodeJS.Timer | number | null;
}

@connect(mapStateToProps)
export default class MediaPage extends React.Component<IMediaPageProps, IMediaPageState> {
	static contextTypes = {
		router: PropTypes.object.isRequired,
	};

	static async activate(
		store,
		{ params: { itemId, assetId, resourceId, mediaId }, location: { search }, staticContext }
	) {
		const { playlistId, reload }: { playlistId?: string; reload?: string } = search ? parseUrl(search, true).query : {};
		resourceId = resourceId ? resourceId.toUpperCase() : null;
		mediaId = mediaId ? mediaId.toUpperCase() : null;
		await store
			.dispatch(getMediaItem({ itemId, assetId, resourceId, mediaId, playlistId }, { reload: reload === 'true' }))
			.catch(logger.error);

		if (staticContext) {
			const {
				mediaItem: { error },
			} = store.getState();
			const statusCode = error?.statusCode;
			if (statusCode === 404) {
				staticContext.statusCode = 404;
			} else if (statusCode) {
				staticContext.statusCode = 500;
			}
		}
	}

	state: IMediaPageState = {
		retryGetMedia: false,
		needsRemount: false,
		autoplayingPlaylist: false,
		reloadItemTimeout: null,
	};

	componentDidMount() {
		const referrer = this.props.referrer.referrer;
		const fromFaithlifeTv = !(referrer === undefined || (!referrer.includes('faithlifetv.com') && referrer !== ''));
		this.setState({
			fromFaithlifeTv,
			supportsMp4: this._supportsVideoType('h264'),
		}); // eslint-disable-line react/no-did-mount-set-state
	}

	componentWillReceiveProps(nextProps: IMediaPageProps) {
		const {
			auth,
			groupMembership,
			mediaItem: { item },
			isEmbedded,
		} = this.props;
		const { auth: nextAuth, groupMembership: nextGroupMembership } = nextProps;
		if (
			!nextAuth.isProcessing &&
			nextAuth.user &&
			nextAuth.user.id !== auth.user.id &&
			nextAuth.user.id !== '-1' &&
			!this.state.retryGetMedia
		) {
			this.setState({ retryGetMedia: true });
			this.reloadItem();
		}
		if (
			!nextGroupMembership.isProcessing &&
			nextGroupMembership.created !== groupMembership.created &&
			nextGroupMembership.created
		) {
			this.reloadItem();
		}
		if (isEmbedded && typeof item?.eventInfo !== 'undefined') {
			parent.postMessage(
				{
					item,
				},
				'*'
			);
		}
	}

	private handleVideoSegmentPlayed = ({ playId, startTime, endTime, bitrate }) => {
		const {
			dispatch,
			mediaItem: { item },
			kidsMode: { isKidsMode },
			isEmbedded,
		} = this.props;
		if (item) {
			dispatch(
				setItemProgress(
					item.id,
					{
						kind: 'segment',
						playId,
						startTime: Math.round(startTime),
						length: Math.round(endTime - startTime),
						bitrate,
					},
					isKidsMode && !isEmbedded
				)
			).catch(logger.error);

			if (this.isLiveStream()) {
				// reload item to refresh viewer count
				const { dispatch, assetId, itemId, mediaId, resourceId } = this.props;
				dispatch(getMediaItem({ assetId, itemId, mediaId, resourceId })).catch(logger.error);
			}
		}
	};

	private handleVideoEnd = () => {
		if (this._getSuggestedVideoEndcap() || this._getUpNext() || this._getRating().canRate) {
			return;
		}
		if (!this.props.isEmbedded) {
			this._navigateToItem();
		}
	};

	reloadItem() {
		const { dispatch, assetId, itemId, mediaId, resourceId } = this.props;
		if (this.state.reloadItemTimeout) {
			this.setState({ reloadItemTimeout: null });
		}
		return dispatch(getMediaItem({ assetId, itemId, mediaId, resourceId }, { reload: true }));
	}

	private handlePlayNextVideo = (nextItemId: string, playlistId: string) => {
		let nextVideoUrl = `/media/${nextItemId}?resume=false`;
		if (playlistId) {
			nextVideoUrl += `&playlistId=${playlistId}`;
		}
		if (this.props.isEmbedded) {
			nextVideoUrl += '&autoplay=1';
		}
		this.setState({ autoplayingPlaylist: true });
		this.context.router.history.push(nextVideoUrl);
	};

	private handleRemountPlayer = (needsRemount: boolean) => {
		this.setState({ needsRemount });
	};

	private handleBack = () => {
		this._navigateToItem();
	};

	private handleError = () => {
		const {
			mediaItem: { item },
		} = this.props;

		if (item && item.kind === 'video-live-stream' && this.state.reloadItemTimeout === null) {
			const refreshInterval = (item.eventInfo && item.eventInfo.refreshInterval) || 10;
			const timeout = setTimeout(() => this.reloadItem(), refreshInterval * 1000);
			this.setState({ reloadItemTimeout: timeout });
		}
	};

	_navigateToItem() {
		const {
			mediaItem: { item },
			playlistId,
		} = this.props;
		if (item && playlistId) {
			this.context.router.history.push('/');
		} else if (item && item.parentId) {
			this.context.router.history.push(`/items/${item.parentId}`);
		} else if (item && item.id) {
			this.context.router.history.push(`/items/${item.id}`);
		}
	}

	_getSuggestedVideoEndcap() {
		const {
			mediaItem: { item },
			isEmbedded,
		} = this.props;
		if (item && item.id && (isEmbedded || item.promotion === 'MOBILE-ED-TV-CHURCH')) {
			const suggestedVideoEndcap = upsells
				.filter(upsell => upsell.itemIds.includes(item.id))
				.map(upsell => upsell.suggestedVideoEndcap)
				.shift();
			// if suggestedVideoEndcap is false, return undefined instead of the default
			const endcap =
				suggestedVideoEndcap != undefined // eslint-disable-line eqeqeq
					? suggestedVideoEndcap || undefined
					: defaultUpsell.suggestedVideoEndcap;
			if (endcap && typeof endcap === 'object' && endcap.suggestions && endcap.suggestions.length) {
				return endcap;
			}
		}
		return undefined;
	}

	_getUpNext() {
		const {
			item: { item: parentItem },
			mediaItem: { item },
			isEmbedded,
			playlistId,
		} = this.props;
		if ((!isEmbedded || (isEmbedded && playlistId)) && item && item.nextItems && item.nextItems.length) {
			const nextItem = item.nextItems[0];
			return {
				nextCover: nextItem.covers[0].url,
				nextItemId: nextItem.id,
				parentItemId: parentItem ? parentItem.id : null,
				startDelay: nextItem.delay,
				title: nextItem.title,
			};
		}
		return undefined;
	}

	_getRating(suggestedVideoEndcap?, upNext?) {
		const {
			mediaItem: { item },
			auth,
			noRating,
		} = this.props;
		return {
			isAuthenticated: auth.user.id !== '-1',
			canRate:
				(auth.user.id !== '-1' && item && !item.promotion && !suggestedVideoEndcap && !upNext && !noRating) || false,
			ratingItemId: (item && item.parentId) || (item && item.id) || '',
			ratingItemTitle: (item && item.parentItem && item.parentItem.title) || (item && item.title),
			returnUrl: item && item.parentId ? `/items/${item.parentId}` : '/',
		};
	}

	_createReturnUrl() {
		const {
			assetId,
			itemId,
			mediaId,
			resourceId,
			location: { search },
		} = this.props;
		const returnUrl = assetId
			? `/media/assets/${assetId}`
			: resourceId && mediaId
			? `/media/resource/${resourceId}/${mediaId}`
			: `/media/${itemId}`;
		let query = { reload: 'true' };
		if (search) {
			query = { ...query, ...parseUrl(search, true).query };
		}
		return formatUrl(returnUrl, query);
	}

	_createGroupMembership(groupInfo: IGroupInfo) {
		const { groupId, action, token } = groupInfo;
		const {
			dispatch,
			auth,
			referrer: { referrer },
			isEmbedded,
		} = this.props;
		const isAuthenticated = !auth.isProcessing && auth.user && auth.user.id !== '-1';
		if (!isAuthenticated) {
			const regex = /^https?:\/\/([^:/]+\.)?faithlife\.com\b/;
			const fromFaithlife = !!(referrer && regex.test(referrer));
			if (isEmbedded && (fromFaithlife || !referrer)) {
				window.open(`${baseUrls.faithlife}/signin?groupToken=${encodeURIComponent(token)}&showRegister=true`, '_top');
			} else {
				const returnUrl = this._createReturnUrl();
				const groupRegistrationRoute = `/register?returnUrl=${encodeURIComponent(
					returnUrl
				)}&groupMembershipId=${encodeURIComponent(groupId)}&groupMembershipAction=${encodeURIComponent(action)}`;
				if (isEmbedded) {
					window.open(`${baseUrls.faithlifeTv}${groupRegistrationRoute}`, '_top');
				} else {
					this.context.router.history.push(groupRegistrationRoute);
				}
			}
		} else if (action === 'join') {
			dispatch(createJoinGroupMembership(groupId)).catch(logger.error);
		} else if (action === 'follow') {
			dispatch(createFollowGroupMembership(groupId)).catch(logger.error);
		}
	}

	_buildInactiveLiveStreamView(customInactiveAsset) {
		if (customInactiveAsset.kind === 'video') {
			return (
				<div style={{ width: '100%', height: '100%', overflow: 'hidden' }} className="inactive-video">
					<VideoPlayer
						sources={[
							{
								url: customInactiveAsset.uri,
							},
						]}
						autoPlay={this.props.isAutoplay}
						className={`{'embedded-player'}`}
						isLiveStream={false}
						ShareOverlayComponent={ShareOverlay}
						onError={this.handleError}
					/>
				</div>
			);
		} else if (customInactiveAsset.kind === 'image') {
			return (
				<div style={{ width: '100%' }} className="inactive-image">
					<img style={{ width: '100%' }} src={customInactiveAsset.uri} alt="inactive live stream" />
				</div>
			);
		}

		return null;
	}

	_renderUnavailable() {
		const {
			auth,
			mediaItem: { item },
			groupMembership,
			isEmbedded,
		} = this.props;
		const promotion = item && item.promotion;
		const user = auth.user;
		const canSignIn = !auth.isProcessing && (!user || !user.id || user.id === '-1');
		const returnUrl = this._createReturnUrl();

		const productLink =
			(item && item.productInfo && item.productInfo.addToCart) ||
			(item && item.productLink && { uri: item.productLink });
		const purchaseInfo = item && item.productInfo && item.productInfo.quickBuy;
		const groupInfo = item && item.groupInfo;
		const eventInfo = item && item.eventInfo;
		const itemTitle = item && item.title;
		const cover = item ? item.covers[0] : undefined;
		const coverUrl = cover && cover.url;
		const orientation = cover && cover.height && cover.width && cover.height > cover.width ? 'portrait' : 'landscape';
		const customInactiveAsset = item?.metadata?.liveStream?.playerSettings?.['customInactiveAsset'];

		const hasGroupMembershipError =
			groupMembership && !groupMembership.isProcessing && !groupMembership.created && groupMembership.error;
		if (hasGroupMembershipError) {
			logger.error(groupMembership.error);
		}
		if (eventInfo && !eventInfo.isOnline && this.state.reloadItemTimeout === null) {
			const refreshInterval = eventInfo.refreshInterval || 10;
			const timeout = setTimeout(() => this.reloadItem(), refreshInterval * 1000);
			this.setState({ reloadItemTimeout: timeout });
		}

		const target = isEmbedded ? '_top' : undefined;

		return promotion === 'PLUS' || purchaseInfo || productLink ? (
			<div>
				{!isEmbedded ? (
					<Link to="/">
						<img className="back-button" src="/public/images/backbutton@2x.png" alt="back" />
					</Link>
				) : null}
				<div className={['promotion', orientation].join(' ')}>
					<h4>{itemTitle}</h4>
					<div className="promotion-image-container">
						<img className="promotion-image" src={coverUrl} alt="promotion" />
						{promotion === 'PLUS' && <PlusRibbon />}
					</div>
					{canSignIn && (
						<div className="promotion-button-container">
							<Link to={`/signin?returnUrl=${encodeURIComponent(returnUrl)}`} target={target}>
								Sign In
							</Link>
						</div>
					)}
				</div>
			</div>
		) : eventInfo && !eventInfo.isOnline ? (
			<div>
				{!isEmbedded ? (
					<Link to="/">
						<img className="back-button" src="/public/images/backbutton@2x.png" alt="back" />
					</Link>
				) : null}
				<div>
					<h4>{itemTitle}</h4>
					{customInactiveAsset ? (
						this._buildInactiveLiveStreamView(customInactiveAsset)
					) : (
						<div className={['promotion', orientation].join(' ')}>
							<div className="promotion-image-container">
								<img className="promotion-image" src={coverUrl} alt="promotion" />
							</div>
							<div className="promotion-button-container">
								<div className="b2" style={{ marginTop: 10 }}>
									The event is not currently live.
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		) : groupInfo && groupInfo.groupRestrictedLicense ? (
			<div>
				{!isEmbedded ? (
					<Link to="/">
						<img className="back-button" src="/public/images/backbutton@2x.png" alt="back" />
					</Link>
				) : null}
				<div className={['promotion', orientation].join(' ')}>
					<h4>{itemTitle}</h4>
					{hasGroupMembershipError ? <h3>Error Creating Group Membership</h3> : null}
					<div className="promotion-image-container">
						<img className="promotion-image" src={coverUrl} alt="promotion" />
					</div>
					<div className="promotion-button-container">
						{groupInfo.action === 'join' && !hasGroupMembershipError ? (
							<button onClick={() => this._createGroupMembership(groupInfo)}>Join to watch</button>
						) : groupInfo.action === 'follow' && !hasGroupMembershipError ? (
							<button onClick={() => this._createGroupMembership(groupInfo)}>Follow to watch</button>
						) : !isEmbedded ? (
							<Link to="/">
								<button>Back to browse</button>
							</Link>
						) : null}
						<div className="b2" style={{ marginTop: 10 }}>
							{groupInfo.name}
						</div>
					</div>
				</div>
			</div>
		) : (
			<div className="error-message">
				<h3>Video not available</h3>
				{canSignIn ? (
					<Link to={`/signin?returnUrl=${encodeURIComponent(returnUrl)}`} target={target}>
						<button>Sign In</button>
					</Link>
				) : !isEmbedded ? (
					<Link to="/">
						<button>Back to browse</button>
					</Link>
				) : null}
			</div>
		);
	}

	_supportsVideoType(type: string): boolean {
		let video;

		const formats = {
			ogg: ['video/ogg; codecs="theora"'],
			h264: ['video/mp4; codecs="avc1.42E01E"', 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"'],
			h265: ['video/mp4; codecs="hvc1.1.L0.0"', 'video/mp4; codecs="hev1.1.L0.0"'],
			webm: ['video/webm; codecs="vp8, vorbis"'],
			vp8: ['video/webm; codecs="vp8"'],
			vp9: ['video/webm; codecs="vp9"'],
			hls: ['application/x-mpegURL; codecs="avc1.42E01E"'],
		};

		if (!video) {
			video = document.createElement('video');
		}

		return (formats[type] || [type]).some(t => video.canPlayType(t));
	}

	private isLiveStream = () => {
		const { mediaItem } = this.props;
		const itemKind = mediaItem.item && mediaItem.item.kind;
		return !!itemKind && ['video-live-stream', 'video-live-event'].includes(itemKind);
	};

	render() {
		const {
			mediaItem,
			subscription,
			isAutoplay,
			groupMembership,
			resume,
			auth,
			isEmbedded,
			playlistId,
			noBug,
			kidsMode,
		} = this.props;
		const item = mediaItem.item;
		const isLiveStream = this.isLiveStream();
		// prefer HLS stream
		const format =
			item && item.formats
				? [
						...(!this.state.supportsMp4 ? item.formats.filter(x => x.type === 'application/dash+xml') : []),
						...item.formats.filter(x => x.type === 'application/x-mpegURL'),
						...item.formats.filter(x => x.type.startsWith('video/')),
				  ].shift()
				: null;
		if (format) {
			format.url = format.url.replace(
				'http://logos-channel.videocdn.scaleengine.net/',
				'https://logos-channel.scaleengine.net/'
			);
			format.url = format.url.replace('http://logos.videocdn.scaleengine.net/', 'https://logos.scaleengine.net/');
			format.url = format.url.replace('http://medstream.logos.com/', 'https://logos.scaleengine.net/');
			format.url = format.url.replace('http://resources.logoscdn.com/', 'https://resources.logoscdn.com/');
			format.url = format.url.replace(/http:\/\/(.*)\.cloudfront\.net\//, 'https://$1.cloudfront.net/');
			format.url = format.url.replace(
				/http:\/\/(.*)\.netformzcdn\.com:1935/,
				'https://5935f232a5aab.streamlock.net:4443'
			);
			format.url = format.url.replace(/http:\/\/(.*)\.streamingchurch\.tv\//, 'https://$1.streamingchurch.tv/');
		}
		const itemDuration = item && item.duration;
		const itemProgress = item && item.progress;
		const startTime =
			this.props.startTime ||
			(resume &&
			itemDuration &&
			itemProgress &&
			itemProgress.kind === 'time' &&
			itemProgress.value &&
			itemProgress.value < itemDuration - 10
				? itemProgress.value
				: 0);
		const isLoading =
			mediaItem.isFetching ||
			typeof mediaItem.isFetching === 'undefined' ||
			(!this.state.retryGetMedia && item && item.error) ||
			(!format && auth.isProcessing) ||
			subscription.isFetching ||
			!!groupMembership.isProcessing;
		const isAvailable = !!format && item && (item.eventInfo == null || item.eventInfo.isOnline !== false);
		const isAuthenticated = auth.user.id !== '-1';

		const windowLocation = isEmbedded && item ? `/media/${encodeURIComponent(item.id)}` : null;

		const suggestedVideoEndcap = this._getSuggestedVideoEndcap();
		const upNext = this._getUpNext();
		const ratingInfo = this._getRating(suggestedVideoEndcap, upNext);

		const itemId = item ? item.id : this.props.itemId;
		const meta = createItemOpenGraphMetadata({
			item,
			itemId,
			playlistId,
			startTime,
		});

		let mediaUrl = `${baseUrls.faithlifeTv}/media/${this.props.itemId}`;
		const mediaUrlQuery: string[] = [];
		if (playlistId) {
			mediaUrlQuery.push(`playlistId=${encodeURI(playlistId)}`);
		}
		if (startTime) {
			mediaUrlQuery.push(`t=${startTime}`);
		}
		if (mediaUrlQuery.length) {
			mediaUrl += '?' + mediaUrlQuery.join('&');
		}
		const link = [createOembedLink(mediaUrl)];

		const showBrandingBug =
			isEmbedded || noBug ? false : !this.state.fromFaithlifeTv && subscription && !subscription.hasSubscription;
		const cover = item && getItemCover({ item, width: 1024 });
		let coverUrl;
		if (cover && isEmbedded) {
			coverUrl = getCoverUrl(cover);
		}

		const customInactiveMedia = item?.metadata?.liveStream?.playerSettings?.['customInactiveAsset'];

		const accentColor = isEmbedded && this.props.accentColor;

		const currentViewerCount = item?.metadata?.liveStream?.playerSettings?.['showCurrentViewerCount']
			? item?.eventInfo?.currentViewerCount
			: null;

		return (
			<div style={{ width: '100%', height: '100%', overflow: 'hidden' }} className="media-page">
				<Helmet title={item && item.title ? item.title : 'Watch'} meta={meta} link={link} />
				{accentColor && (
					<style>{`
.video-js .vjs-volume-level {
	background-color: #${accentColor};
}

.video-js .vjs-play-progress {
	background-color: #${accentColor};
}
`}</style>
				)}
				{isLoading && !this.state.autoplayingPlaylist && !customInactiveMedia ? (
					<LoadingIndicator />
				) : !isAvailable && !this.state.autoplayingPlaylist ? (
					this._renderUnavailable()
				) : this.state.needsRemount ? null : (
					<VideoPlayer
						sources={[
							{
								url: format ? format.url : null,
								mediaType: format ? format.type : null,
							},
						]}
						className={`${isEmbedded && 'embedded-player'}`}
						videoId={item ? item.id : null}
						startTime={startTime}
						title={item ? item.title : null}
						isLiveStream={isLiveStream}
						liveStreamActiveViewerCount={currentViewerCount}
						autoPlay={isAutoplay}
						isEmbedded={isEmbedded}
						playlistId={playlistId}
						onVideoSegmentPlayed={this.handleVideoSegmentPlayed}
						onVideoEnd={this.handleVideoEnd}
						onBack={this.handleBack}
						windowLocation={windowLocation}
						suggestedVideoEndcap={suggestedVideoEndcap}
						upNext={upNext}
						onPlayNextVideo={this.handlePlayNextVideo}
						useGoogleAnalytics
						useAmplitude
						ratingInfo={ratingInfo}
						showBrandingBug={showBrandingBug}
						brandingBugCampaign={launch7Campaign}
						posterUrl={coverUrl}
						EndCapComponent={EndCap}
						UpNextComponent={UpNext}
						RatingOverlayComponent={
							isAuthenticated ? (kidsMode.isKidsMode && !isEmbedded ? KidsRatingOverlay : RatingOverlay) : null
						}
						ShareOverlayComponent={ShareOverlay}
						remountPlayer={this.handleRemountPlayer}
						onError={this.handleError}
					/>
				)}
				<script src="//www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1"></script>
			</div>
		);
	}
}
